export interface GlobalSearchDestination {
  view: string;
  label: string;
  group: string;
}

function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase('fr');
}

export function filterGlobalSearchDestinations(
  destinations: GlobalSearchDestination[],
  query: string,
): GlobalSearchDestination[] {
  const normalizedQuery = normalize(query.trim());
  if (!normalizedQuery) return [];
  return destinations
    .filter((destination) =>
      normalize(`${destination.label} ${destination.group} ${destination.view}`).includes(
        normalizedQuery,
      ),
    )
    .slice(0, 8);
}

function visibleDestinations(root: HTMLElement): GlobalSearchDestination[] {
  return Array.from(root.querySelectorAll<HTMLElement>('.sidebar a[data-view]'))
    .filter(
      (link) =>
        link.style.display !== 'none' &&
        link.closest<HTMLElement>('.nav-group')?.style.display !== 'none',
    )
    .map((link) => ({
      view: link.dataset.view ?? '',
      label: link.textContent?.trim() ?? '',
      group:
        link
          .closest<HTMLElement>('.nav-group')
          ?.querySelector<HTMLElement>('.nav-section')
          ?.textContent?.trim() ?? '',
    }))
    .filter((destination) => destination.view && destination.label);
}

export function wireGlobalSearch(
  root: HTMLElement,
  navigate: (view: string) => void,
): (() => void) | undefined {
  const input = root.querySelector<HTMLInputElement>('#global-search');
  const results = root.querySelector<HTMLElement>('#global-search-results');
  if (!input || !results) return undefined;

  let matches: GlobalSearchDestination[] = [];
  let activeIndex = -1;

  const close = (): void => {
    results.hidden = true;
    results.replaceChildren();
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    activeIndex = -1;
  };

  const updateActiveResult = (): void => {
    results.querySelectorAll<HTMLElement>('[role="option"]').forEach((option, index) => {
      option.classList.toggle('active', index === activeIndex);
      option.setAttribute('aria-selected', String(index === activeIndex));
    });
    if (activeIndex >= 0) {
      input.setAttribute('aria-activedescendant', `global-search-result-${activeIndex}`);
    } else {
      input.removeAttribute('aria-activedescendant');
    }
  };

  const choose = (destination: GlobalSearchDestination): void => {
    input.value = '';
    close();
    navigate(destination.view);
  };

  const render = (): void => {
    matches = filterGlobalSearchDestinations(visibleDestinations(root), input.value);
    results.replaceChildren();
    activeIndex = matches.length > 0 ? 0 : -1;

    if (!input.value.trim()) {
      const hint = document.createElement('p');
      hint.className = 'global-search-empty';
      hint.setAttribute('role', 'status');
      hint.textContent = 'Saisissez le nom d’un écran (ex. salariés, bulletins…)';
      results.replaceChildren(hint);
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      return;
    }

    if (matches.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'global-search-empty';
      empty.setAttribute('role', 'status');
      empty.textContent = 'Aucun écran trouvé';
      results.append(empty);
    } else {
      matches.forEach((destination, index) => {
        const option = document.createElement('button');
        option.type = 'button';
        option.id = `global-search-result-${index}`;
        option.className = 'global-search-result';
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', String(index === activeIndex));

        const label = document.createElement('strong');
        label.textContent = destination.label;
        const group = document.createElement('small');
        group.textContent = destination.group;
        option.append(label, group);
        option.addEventListener('mousedown', (event) => event.preventDefault());
        option.addEventListener('mouseenter', () => {
          activeIndex = index;
          updateActiveResult();
        });
        option.addEventListener('click', () => choose(destination));
        results.append(option);
      });
    }

    results.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    updateActiveResult();
  };

  const handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (matches.length === 0) return;
      event.preventDefault();
      const offset = event.key === 'ArrowDown' ? 1 : -1;
      activeIndex = (activeIndex + offset + matches.length) % matches.length;
      updateActiveResult();
      return;
    }
    if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      const destination = matches[activeIndex];
      if (destination) choose(destination);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      input.value = '';
      close();
    }
  };

  const handleBlur = (): void => {
    window.setTimeout(close, 100);
  };

  input.addEventListener('input', render);
  input.addEventListener('focus', render);
  input.addEventListener('keydown', handleKeyDown);
  input.addEventListener('blur', handleBlur);

  return () => {
    input.removeEventListener('input', render);
    input.removeEventListener('focus', render);
    input.removeEventListener('keydown', handleKeyDown);
    input.removeEventListener('blur', handleBlur);
  };
}
